/**
 * KestrelInitializer
 *
 * Handles side effects for the KestrelViewer that depend on the PixelStreaming
 * connection. This component renders nothing—it exists purely for its side effects.
 *
 * Responsibilities:
 * - Listen for `videoInitialized` event and send initialization commands
 * - Initialize session metrics tracking
 * - Handle keyboard shortcuts (K key for layer switching)
 * - Handle temperature probe (right-click in temperature mode)
 * - Manage stream resolution based on viewport size
 */
import { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useGeckoPermissions } from 'features/permissions'
import { FC, RefObject, useCallback, useEffect, useRef } from 'react'
import { KVDataLayerConfig } from 'types'

import { useStreamResolution } from './hooks/useStreamResolution'
import { useKestrel } from './KestrelProvider'
import { useKestrelViewerContext } from './KestrelViewerProvider'
import {
  calculateInspectableSqft,
  cleanupMetricsStorage,
  createSessionMetrics,
  initializeMetricsStorage,
  markLoadComplete,
  saveSessionMetricsToRolodex,
  startKestrelSession,
  startWebRTCStatsCollection,
} from './metrics'
import { KestrelCommands } from './types/KestrelCommands'
import type { KVActiveLayer } from './types/kestrelViewerTypes'
import { initializationCommands } from './utils/initializationCommands'
import { getDataLayersVisibility } from './utils/visibilityUtils'

const TEMPERATURE_PROBE_TIMEOUT_MS = 3000

interface KestrelInitializerProps {
  pixelStreaming: PixelStreaming | undefined
  videoParentRef: RefObject<HTMLDivElement | null>
}

// ============================================================================
// Helper functions
// ============================================================================

const isValidLayerType = (type: string): type is 'rgb' | 'visual' | 'raw' => {
  return type === 'rgb' || type === 'visual' || type === 'raw'
}

const DEFAULT_TEMPERATURE_MIN = -100
const DEFAULT_TEMPERATURE_MAX = 300
const DEFAULT_LAYER_OPACITY = 1.0

const emitLayerCommand = (
  pixelStreaming: PixelStreaming,
  layer: KVActiveLayer,
  isMultiObjectModel: boolean
) => {
  if (layer.type === 'rgb') {
    pixelStreaming.emitUIInteraction({
      command: KestrelCommands.SHOW_LAYER_RGB,
      prefix: layer.prefix,
      opacity: layer.opacity || DEFAULT_LAYER_OPACITY,
      use_multi_obj_model: isMultiObjectModel,
    })
  } else if (layer.type === 'visual') {
    pixelStreaming.emitUIInteraction({
      command: KestrelCommands.SHOW_LAYER_VISUAL,
      prefix: layer.prefix,
      opacity: layer.opacity || DEFAULT_LAYER_OPACITY,
      use_multi_obj_model: isMultiObjectModel,
    })
  } else if (layer.type === 'raw') {
    const decodeMin = layer.decode_min_f ?? DEFAULT_TEMPERATURE_MIN
    const decodeMax = layer.decode_max_f ?? DEFAULT_TEMPERATURE_MAX
    pixelStreaming.emitUIInteraction({
      command: KestrelCommands.SHOW_LAYER_RAW,
      prefix: layer.prefix,
      decode_min_f: decodeMin,
      decode_max_f: decodeMax,
      visible_min_f: layer.visible_min_f ?? decodeMin,
      visible_max_f: layer.visible_max_f ?? decodeMax,
      opacity: layer.opacity || DEFAULT_LAYER_OPACITY,
      gradient: layer.gradient,
      use_multi_obj_model: isMultiObjectModel,
    })
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * @internal Implementation detail of KestrelViewerProvider.
 * Do not use directly—it's rendered automatically by the provider.
 */
export const KestrelInitializer: FC<KestrelInitializerProps> = ({
  pixelStreaming,
  videoParentRef,
}) => {
  const { authorization } = useGeckoAuth()
  const { user = { name: '', email: '' } } = useGeckoPermissions()
  const { KESTREL_API_URL, ROLODEX_URL } = useRuntimeVariables()

  const {
    kestrelModel,
    historicalAnnotations,
    cameraPoses,
    gatekeeper,
    inspectionAreas,
    setVideoReady,
    kestrelStatus,
    inspectionType,
  } = useKestrel()

  const {
    selectedMode,
    measurementType,
    navigationMode,
    is2DPlaneModeActive,
    currentUnit,
    settings,
    thermalGradientVisible,
    activeLayer,
    setActiveLayer,
    resolutionQuality,
    setTemperatureProbeLoading,
  } = useKestrelViewerContext()

  // Track active layer for keyboard handler (avoid stale closure)
  const activeLayerRef = useRef<KVActiveLayer>(activeLayer)
  useEffect(() => {
    activeLayerRef.current = activeLayer
  }, [activeLayer])

  // Compute hasDataLayersSupport from model
  const hasDataLayersSupport = getDataLayersVisibility(kestrelModel)

  // Stream resolution management
  useStreamResolution(
    pixelStreaming,
    videoParentRef,
    resolutionQuality,
    kestrelStatus?.videoReady ?? false
  )

  // ==========================================================================
  // Video Initialization
  // ==========================================================================

  useEffect(() => {
    if (!pixelStreaming) return

    const handleVideoInitialized = () => {
      // Only initialize once per session; reset happens when setVideoReady(false)
      if (kestrelStatus?.videoReady) {
        return
      }

      const authToken = authorization?.split(' ')[1]

      // Send initialization commands to Unreal
      initializationCommands({
        pixelStreaming,
        kestrelModel: kestrelModel ?? null,
        historicalAnnotations,
        cameraPoses,
        gatekeeper,
        hasDataLayersSupport,
        thermalGradientVisible,
        navigationMode,
        selectedMode,
        measurementType,
        is2DPlaneModeActive,
        currentUnit,
        settings,
        authToken,
        rolodexUrl: ROLODEX_URL,
        kestrelApiUrl: KESTREL_API_URL,
        userEmail: user.email,
        userName: user.name,
        inspectionAreas,
      })

      // Mark load metrics complete
      markLoadComplete()

      // Signal video ready
      setVideoReady(true)

      // Start session tracking now that video is ready
      const inspectableSqft = calculateInspectableSqft(
        kestrelModel?.data.inspectionAreas
      )

      // Initialize metrics storage for Rolodex persistence
      if (authToken && kestrelModel?.id && ROLODEX_URL) {
        initializeMetricsStorage(ROLODEX_URL, authToken, kestrelModel.id, {
          organization: kestrelModel.tags.organization,
          site: kestrelModel.tags.site,
        })
      }

      // Session end callback to save metrics to Rolodex
      const handleSessionEnd = (metrics: {
        userEmail: string
        inspectableSqft: number | null
        sessionDurationMs: number
        activeTimeMs: number
        annotationsCreated: number
        measurementsCreated: number
        reportsGenerated: number
        dataValidationTotalTimeMs: number
        dataValidationTimeToExhaustMs: number | null
        dataValidationAnnotationCount: number | null
        reportExtensionTotalTimeMs: number
        reportTextToGenerateMs: number | null
      }) => {
        const sessionMetrics = createSessionMetrics(metrics)
        saveSessionMetricsToRolodex(sessionMetrics)
        cleanupMetricsStorage()
      }

      startKestrelSession(
        {
          vmId: kestrelStatus?.vmID ?? null,
          kestrelModelId: kestrelModel?.id ?? '',
          userEmail: user.email ?? '',
          inspectionType: inspectionType,
          inspectableSqft: inspectableSqft ?? undefined,
          unitId: kestrelModel?.tags.unit,
        },
        handleSessionEnd
      )

      startWebRTCStatsCollection(pixelStreaming)
    }

    pixelStreaming.addEventListener('videoInitialized', handleVideoInitialized)
    return () => {
      pixelStreaming.removeEventListener(
        'videoInitialized',
        handleVideoInitialized
      )
    }
  }, [
    pixelStreaming,
    kestrelStatus?.videoReady,
    kestrelStatus?.vmID,
    setVideoReady,
    authorization,
    ROLODEX_URL,
    KESTREL_API_URL,
    user.email,
    user.name,
    kestrelModel,
    historicalAnnotations,
    cameraPoses,
    gatekeeper,
    inspectionAreas,
    hasDataLayersSupport,
    thermalGradientVisible,
    navigationMode,
    selectedMode,
    measurementType,
    is2DPlaneModeActive,
    currentUnit,
    settings,
    inspectionType,
  ])

  // ==========================================================================
  // Layer Switching (K key)
  // ==========================================================================

  const handleLayerSwitch = useCallback(
    (event: KeyboardEvent) => {
      if (!pixelStreaming) return

      if (
        event.key.toLowerCase() === 'k' &&
        getDataLayersVisibility(kestrelModel) &&
        kestrelModel?.data?.dataLayersConfig?.layerSwitchingEnabled
      ) {
        event.preventDefault()

        const config = kestrelModel?.data?.dataLayersConfig
        if (!config) return

        const currentLayer = activeLayerRef.current
        const currentLayerIndex = config.layers.findIndex(
          (layer: KVDataLayerConfig) =>
            layer.prefix === currentLayer.prefix &&
            layer.type === currentLayer.type
        )

        if (currentLayerIndex === -1 || config.layers.length === 0) {
          return
        }

        const nextLayerIndex = (currentLayerIndex + 1) % config.layers.length
        const nextLayerConfig = config.layers[nextLayerIndex]

        if (!isValidLayerType(nextLayerConfig.type)) {
          return
        }

        const newLayer: KVActiveLayer = {
          ...currentLayer,
          prefix: nextLayerConfig.prefix,
          type: nextLayerConfig.type,
          decode_min_f: nextLayerConfig.decodeRange?.min_f,
          decode_max_f: nextLayerConfig.decodeRange?.max_f,
          visible_min_f:
            nextLayerConfig.type === 'raw'
              ? nextLayerConfig.decodeRange?.min_f
              : undefined,
          visible_max_f:
            nextLayerConfig.type === 'raw'
              ? nextLayerConfig.decodeRange?.max_f
              : undefined,
          isCustom: nextLayerConfig.isCustom,
          customId: nextLayerConfig.customId,
        }
        setActiveLayer(newLayer)

        const isMultiObjectModel =
          kestrelModel?.data?.dataLayersConfig?.isMultiObjectModel ?? false
        emitLayerCommand(pixelStreaming, newLayer, isMultiObjectModel)
      }
    },
    [pixelStreaming, kestrelModel, setActiveLayer]
  )

  useEffect(() => {
    if (!pixelStreaming || !kestrelStatus?.videoReady) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return
      }
      handleLayerSwitch(event)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [pixelStreaming, kestrelStatus?.videoReady, handleLayerSwitch])

  // Emit current layer when model layer config changes
  useEffect(() => {
    if (!pixelStreaming || !kestrelStatus?.videoReady) return
    if (!hasDataLayersSupport) return
    if (!kestrelModel?.data?.dataLayersConfig) return

    const isMultiObjectModel =
      kestrelModel?.data?.dataLayersConfig?.isMultiObjectModel ?? false
    emitLayerCommand(pixelStreaming, activeLayerRef.current, isMultiObjectModel)
  }, [
    pixelStreaming,
    kestrelStatus?.videoReady,
    hasDataLayersSupport,
    kestrelModel?.data?.dataLayersConfig,
  ])

  // ==========================================================================
  // Temperature Probe (right-click in temperature mode)
  // ==========================================================================

  const handleTemperatureProbe = useCallback(
    (event: MouseEvent) => {
      if (!pixelStreaming) return

      const shouldHandle = selectedMode === 'temperature' && event.button === 2
      if (shouldHandle) {
        event.preventDefault()
        event.stopPropagation()

        setTemperatureProbeLoading(true)

        if (gatekeeper.feature.temperatureProbeUsesCursorData) {
          pixelStreaming.emitUIInteraction({
            command: KestrelCommands.GET_CURSOR_DATA,
          })
        } else {
          pixelStreaming.emitUIInteraction({
            command: KestrelCommands.REQUEST_TEMPERATURE_PROBE,
          })
        }

        setTimeout(() => {
          setTemperatureProbeLoading(false)
        }, TEMPERATURE_PROBE_TIMEOUT_MS)
      }
    },
    [pixelStreaming, selectedMode, gatekeeper, setTemperatureProbeLoading]
  )

  useEffect(() => {
    if (
      !videoParentRef.current ||
      !pixelStreaming ||
      !kestrelStatus?.videoReady
    )
      return

    const handleMouseDown = (event: MouseEvent) => {
      handleTemperatureProbe(event)
    }

    const videoElement = videoParentRef.current
    videoElement.addEventListener('mousedown', handleMouseDown)

    return () => {
      videoElement.removeEventListener('mousedown', handleMouseDown)
    }
  }, [
    pixelStreaming,
    kestrelStatus?.videoReady,
    handleTemperatureProbe,
    videoParentRef,
  ])

  // This component renders nothing - it's purely for side effects
  return null
}
